"use client";

import { forwardRef, useId, useRef, useState } from "react";
import styles from "./TextField.module.css";

const sizeClass = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

const TextField = forwardRef(function TextField(
  {
    label,
    helperText,
    errorText,
    size = "md",
    type = "text",
    multiline = false,
    iconLeft,
    iconRight,
    kbd,
    disabled = false,
    required = false,
    fullWidth = true,
    className = "",
    id,
    onFocus,
    onBlur,
    ...inputProps
  },
  ref
) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const innerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const setInputRef = (el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  const showError = Boolean(errorText);
  const description = showError ? errorText : helperText;

  const fieldClasses = [styles.field, fullWidth ? "" : styles.fitContent, className]
    .filter(Boolean)
    .join(" ");

  const wrapClasses = [
    styles.inputWrap,
    sizeClass[size] ?? sizeClass.md,
    multiline ? styles.multiline : "",
    isFocused ? styles.focused : "",
    showError ? styles.error : "",
    disabled ? styles.disabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const Control = multiline ? "textarea" : "input";
  const controlProps = {
    ref: setInputRef,
    id: inputId,
    disabled,
    required,
    className: styles.input,
    "aria-invalid": showError || undefined,
    "aria-describedby": description ? `${inputId}-desc` : undefined,
    onFocus: (event) => {
      setIsFocused(true);
      onFocus?.(event);
    },
    onBlur: (event) => {
      setIsFocused(false);
      onBlur?.(event);
    },
    ...inputProps,
  };

  if (!multiline) {
    controlProps.type = type;
  }

  return (
    <div className={fieldClasses}>
      {label ? (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required ? <span className={styles.required}>*</span> : null}
        </label>
      ) : null}

      <div
        className={wrapClasses}
        onClick={() => {
          if (!disabled) innerRef.current?.focus();
        }}
      >
        {iconLeft ? (
          <span className={`${styles.icon} ${styles.iconLeft}`} aria-hidden="true">
            {iconLeft}
          </span>
        ) : null}

        <Control {...controlProps} />

        {iconRight ? (
          <span className={`${styles.icon} ${styles.iconRight}`} aria-hidden="true">
            {iconRight}
          </span>
        ) : null}

        {kbd ? (
          <span className={styles.kbd} aria-hidden="true">
            {(typeof kbd === "string" ? Array.from(kbd) : kbd).map((key, index) => (
              <span key={index} className={styles.kbdKey}>
                {key}
              </span>
            ))}
          </span>
        ) : null}
      </div>

      {description ? (
        <div
          id={`${inputId}-desc`}
          className={showError ? styles.errorText : styles.helperText}
        >
          {description}
        </div>
      ) : null}
    </div>
  );
});

export default TextField;
